import { Grid, Typography, styled, tableCellClasses } from '@mui/material';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import { useQuery } from '@tanstack/react-query';
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { privateClient } from '../../axiosClients/axiosClients';
import { handleFileDownload } from '../../helpers/filesHelpers/downloadFiles';
import { createGetOverviewStepsOptions } from '../../queries/overviewQueries';
import { errorNotification } from '../../reducers/handle-api-status/slice';
import { nameDiv } from '../../styles';
import Button from '../shared/Button';

export default function TrackingFileTable() {
  const [searchParams] = useSearchParams();
  const [files, setFiles] = useState([]);
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const pecrId = searchParams.get('id');
  const site = searchParams.get('site');
  const step = searchParams.get('step');
  let { data } = useQuery(
    createGetOverviewStepsOptions({ pecrId: pecrId }, { enabled: !!pecrId })
  );
  const filterFilesOnly = files => {
    return files?.filter(file => file.includes('.')) || [];
  };

  useEffect(() => {
    const fetchFilesForStep = async siteInfo => {
      const baseParams = {
        MainFolder: 'PECROverview',
        SubFolder: siteInfo.PECRID,
      };

      try {
        let response;

        if (
          siteInfo.Step === 'Creator' ||
          siteInfo.Step === 'Coordinator review before estimates'
        ) {
          response = await privateClient.post('/FetchFiles', baseParams);
        } else if (siteInfo.Step === 'Site Coordinator Review') {
          response = await privateClient.post('/FetchFilesFromSiteReview', {
            ...baseParams,
            siteId: siteInfo.SiteID,
            siteReview: 'SiteReview',
          });
        } else if (siteInfo.Step === 'Owner Review') {
          response = await privateClient.post('/FetchFilesFromOwner', {
            ...baseParams,
            siteId: siteInfo.SiteID,
            owner: 'Owner',
          });
        } else if (siteInfo.Step === 'Estimates') {
          response = await privateClient.post('/FetchFilesFromDepartment', {
            ...baseParams,
            siteId: siteInfo.SiteID,
            departmentId: siteInfo.DepartmentID,
            index: siteInfo.DepartmentIndex,
          });
        } else if (siteInfo.Step === 'Execution') {
          response = await privateClient.post(
            '/FetchFilesFromDepartmentExecution',
            {
              ...baseParams,
              siteId: siteInfo.SiteID,
              departmentId: siteInfo.DepartmentID,
              index: siteInfo.DepartmentIndex,
              pecrId: siteInfo.PECRID,
            }
          );
        }

        if (response?.data) {
          return filterFilesOnly(response.data);
        }
      } catch (error) {
        dispatch(
          errorNotification({
            hasErrorOccured: true,
            message: t('error_message'),
          })
        );
      }
      return [];
    };

    const processSteps = async () => {
      if (!data?.length) {
        return;
      }

      setFiles([]); // Clear existing files

      const newFiles = [];
      for (const siteInfo of data) {
        if (
          siteInfo.Step === 'Coordinator review before execution' ||
          siteInfo.Step === 'Coordinator Review'
        ) {
          newFiles.push([]);
        } else {
          const stepFiles = await fetchFilesForStep(siteInfo);
          newFiles.push(stepFiles);
        }
      }
      setFiles(newFiles);
    };

    processSteps();
  }, [data, dispatch, t]);
  // Files state is now managed in the order they appear in the data array

  const columns = [
    { id: 'Site', label: t('Site') },
    { id: 'Step', label: t('Step') },
    { id: 'Actor', label: t('Actor') },
    { id: 'Department', label: t('Department') },
    { id: 'Record Date', label: t('Record_date') },
    { id: 'Date', label: t('Target_Date') },
    { id: 'Comments', label: t('Comments') },
    { id: 'Files', label: t('Files') },
    { id: 'Additional Costs', label: t('Additional_Costs') },
    { id: 'Effort', label: t('Effort') },
    { id: 'Status', label: t('status') },
  ];

  const pecrStatus = {
    1: 'Open',
    2: 'LSC Review',
    3: 'Approved',
    4: 'Approved',
    5: 'In Process',
    6: 'Resolved',
    7: 'Cancelled',
    8: 'Approved',
    9: 'Hold',
    10: 'Completed',
    11: 'Not Impacted',
    12: 'Approved',
    13: 'Submitted',
    14: 'Rejected',
    15: 'Completed',
    16: 'Completed',
    null: 'In Process',
    '': 'In Process',
  };

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(100);
  // Column ordering removed as it's not being used

  function handleDateConversion(date) {
    if (!date) {
      return '';
    }
    //default null date for sql
    if (date.includes('1970')) {
      return '';
    }
    return new Date(date).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  }

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = event => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  function handlePecrStatusConversion(status, step) {
    if (Number(status) === 15 && step === 'Coordinator Review') {
      return 'In Process';
    }
    if (Number(status) === 0 && step === 'Coordinator Review') {
      return 'In Process';
    }
    if (Number(status) === 2 && step === 'Creator') {
      return 'Submitted';
    }
    return pecrStatus[status];
  }

  function handleComments(comments, EOComments) {
    if (comments && EOComments) {
      return (
        <>
          Comments: {comments || ''} <br />
          <br />
          Excess and Obsolete:{' '}
          {EOComments.trim() !== ''
            ? EOComments
            : 'No Excess and Obsolete comments'}
        </>
      );
    }
    return comments || '';
  }
  function handleCostAndEffort(data, type) {
    if (data[type] !== '' && data[type] !== null && data.ActualStatus !== 11) {
      return type === 'Cost'
        ? `${data.Cost}${data.Currency}`
        : String(data.Effort).concat('h');
    }
    return '';
  }

  //set the page index as 0 whenever rows(list) is changing
  useEffect(() => {
    setPage(0);
  }, [data]);

  const StyledTableRow = styled(TableRow)(({ theme }) => ({
    '&:nth-of-type(even)': {
      backgroundColor: theme.palette.action.hover,
    },
  }));
  if (!data) {
    return null;
  }
  if (data && data.length > 0 && site && step) {
    if (site !== 'all' && step === 'all') {
      data = data.filter(item => item.SiteName === site);
    }
    if (site !== 'all' && step !== 'all') {
      data = data.filter(item => item.SiteName === site && item.Step === step);
    }
  }
  let cleanedData = data;
  if (data && data.length > 0) {
    const coordReviewBeforeExec = data.filter(
      item => item.Step === 'Coordinator review before execution'
    );

    if (coordReviewBeforeExec.length > 0) {
      const incompleteSites = coordReviewBeforeExec
        .filter(
          item =>
            item.ActualStatus === null ||
            item.ActualStatus === '' ||
            Number(item.ActualStatus) === 5
        )
        .map(item => item.SiteID);

      cleanedData = data.filter(
        item =>
          !(item.Step === 'Execution' && incompleteSites.includes(item.SiteID))
      );
    }
  }

  function handleShowAllSteps() {
    const params = new URLSearchParams(location.search);
    params.set('site', 'all');
    params.set('step', 'all');
    navigate({ search: params.toString() });
  }

  return (
    <Paper sx={{ width: '100%', overflow: 'hidden' }}>
      <Button
        message="Show All Steps"
        type="primary"
        onClick={handleShowAllSteps}
      />
      <TableContainer
        sx={{
          maxHeight: 600,
          maxWidth: 'screen',
          marginTop: '4px',
        }}
      >
        <Table stickyHeader aria-label="sticky table">
          <TableHead>
            <TableRow>
              {columns.map(column => (
                <TrackingFileTableHeader
                  key={column.id}
                  id={column.id}
                  label={column.label}
                />
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {cleanedData &&
              cleanedData
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((row, index) => {
                  return (
                    <StyledTableRow
                      hover
                      role="checkbox"
                      tabIndex={-1}
                      key={index}
                    >
                      <TrackingFileTableItem data={row.SiteName} />
                      <TrackingFileTableItem data={row.Step} />
                      <TrackingFileTableItem data={row.UserName} />
                      <TrackingFileTableItem
                        data={`${row.Department ?? ''} ${row.SubDepartmentName ? '<' : ''}${row.SubDepartmentName ?? ''}${row.SubDepartmentName ? '>' : ''}`}
                      />
                      <TrackingFileTableItem
                        data={handleDateConversion(row.ReviewDate)}
                      />
                      <TrackingFileTableItem
                        data={handleDateConversion(row.TargetDate)}
                      />
                      <TrackingFileTableItem
                        data={handleComments(row.Comments, row.EOComments)}
                      />
                      <TrackingFileTableItem
                        data={files[index]?.length ? files[index] : ['']}
                        rowData={row}
                        isFile
                      />
                      <TrackingFileTableItem
                        data={handleCostAndEffort(row, 'Cost')}
                      />
                      <TrackingFileTableItem
                        data={handleCostAndEffort(row, 'Effort')}
                      />
                      <TrackingFileTableItem
                        data={handlePecrStatusConversion(
                          row.ActualStatus,
                          row.Step
                        )}
                        link={row.ToDoLink}
                        user={row.UserID}
                      />
                    </StyledTableRow>
                  );
                })}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[10, 25, 100]}
        component="div"
        count={cleanedData.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        labelRowsPerPage={`${t('Rows_per_page')}:`}
        sx={{ boxShadow: 'inset 0 0 2px #000000' }}
      />
    </Paper>
  );
}

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: '#1F305E',
    color: theme.palette.common.white,
    fontWeight: 'bold',
    fontSize: '17px',
    position: 'sticky',
    zIndex: 2,
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: '17px',
  },
}));

function TrackingFileTableItem({
  data,
  rowData,
  user,
  isFile = false,
  link = null,
}) {
  const userInfo = useSelector(state => state.userInfo);
  const navigate = useNavigate();
  if (isFile) {
    return (
      <StyledTableCell align="center">
        {data &&
          data.map(file => {
            return (
              <>
                <Typography
                  key={file}
                  onClick={() => {
                    handleFileDownload(
                      rowData.Step,
                      rowData.PECRID,
                      rowData.SiteID,
                      rowData.DepartmentID,
                      rowData.DepartmentIndex,
                      file
                    );
                  }}
                  sx={{
                    cursor: 'pointer',
                    textAlign: 'center',
                    color: '#0a58ca',
                    textDecoration: 'underline',
                    maxWidth: 300,
                    maxHeight: 100,
                    overflowY: 'auto',
                    overflowX: 'hidden',
                  }}
                >
                  {file}
                </Typography>
                <br />
              </>
            );
          })}
      </StyledTableCell>
    );
  }
  if (link) {
    const isUser = user === userInfo?.userID;
    const isInProcess = data === 'In Process' || data === 'LSC Review';
    const isDisabled = !isUser || !isInProcess;

    return (
      <StyledTableCell align="center">
        <Typography
          aria-disabled={isDisabled}
          sx={{
            cursor: isDisabled ? 'default' : 'pointer',
            textAlign: 'center',
            color: !isDisabled && '#0a58ca',
            textDecoration: isDisabled ? 'none' : 'underline',
            maxWidth: 300,
            maxHeight: 100,
            overflowY: 'auto',
            overflowX: 'hidden',
          }}
          onClick={() => {
            if (!isDisabled) {
              navigate(`${link}`);
            }
          }}
        >
          {data}
        </Typography>
      </StyledTableCell>
    );
  }
  return (
    <StyledTableCell align="center">
      <Typography
        sx={{
          cursor: 'default',
          textAlign: 'center',
          maxWidth: 500,
          maxHeight: 200,
          overflowY: 'auto',
          overflowX: 'hidden',
        }}
      >
        {data}
      </Typography>
    </StyledTableCell>
  );
}

TrackingFileTableItem.propTypes = {
  data: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  rowData: PropTypes.object,
  isFile: PropTypes.bool,
  link: PropTypes.string,
};

function TrackingFileTableHeader({ id, label }) {
  return (
    <StyledTableCell key={id} align="center">
      <Grid style={nameDiv}>
        <Grid>{label}</Grid>
      </Grid>
    </StyledTableCell>
  );
}

TrackingFileTableHeader.propTypes = {
  id: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
};
